import { z } from "zod";
import type { PipelineNode, PipelineSpec, ToolCore, VideoBgRemoveProvider } from "@loumy/contracts";
import { CAP } from "@loumy/capabilities";

// §10.3 ChromaKey-Remover. Media-Union (D132 — vorher Bild-only, D45), server-only.
// Bild: nur `chroma-key(image)` direkt auf den Upload — KEINE Generate-Phase → startet
// direkt in der Server-Post-Process-Phase. Video (D132): `chroma-key(video)` (die
// Capability decodiert selbst, D38) → `video-encode` → transparentes WebM.
//
// D146: Video-Pfad kann statt chroma-key (Greenscreen-Annahme, GRATIS) auch **AI-Matting**
// (BEN2 via fal, `method='ai'`, bezahlt) — braucht KEINEN Greenscreen, saubere Alpha-Kanten.
// Beide münden in `video-encode` → transparentes WebM (identischer Output-Typ). Bild-Pfad
// bleibt chroma-only (AI-Matting auf Einzelbildern = das eigenständige bg-remove-Tool, Bria).

// D132: decode-/encode-fps des Video-Pfads — Build-Literal (kein Setting v1, §13). MUSS in
// key- UND encode-Knoten identisch eingefroren werden (gleiche Dauer-Semantik; beide Werte
// gehen in die deterministischen GPU-Keys).
const VIDEO_FPS = 24;

export const ChromaKeyRemoverSettings = z.object({
  // D146: 'chroma' = Greenscreen-Keying (gratis, Default); 'ai' = BEN2 AI-Matting (bezahlt,
  // kein Greenscreen nötig). Wirkt NUR auf den Video-Pfad — der Bild-Pfad ist immer chroma.
  method: z.enum(["chroma", "ai"]).default("chroma"),
  tolerance: z.number().min(0).max(100).default(20),
  keyColor: z.enum(["auto", "green", "blue"]).default("auto"),
});
export type ChromaKeyRemoverSettings = z.infer<typeof ChromaKeyRemoverSettings>;

export const chromaKeyRemoverCore: ToolCore<ChromaKeyRemoverSettings> = {
  id: "chroma-key-remover",
  category: "image",
  // D132: Slot-Union image|video — `kind` bleibt Anzeige/Default, `kinds` öffnet den Upload
  // (MediaPanel-accept) und den Submit (`resolveInputs` prüft gegen die Union und reicht den
  // ECHTEN Asset-Kind in die AssetRefs, auf dem buildPipeline unten brancht).
  inputs: [{ kind: "image", kinds: ["image", "video"], min: 1, max: 1, role: "source" }],
  settingsSchema: ChromaKeyRemoverSettings,
  // D132: je Build ist genau EIN Slot belegt (image-Pfad → image, video-Pfad → video).
  outputSlots: [
    { name: "image", kind: "image", optional: true },
    { name: "video", kind: "video", optional: true },
  ],
  buildPipeline(settings, inputs, ctx): PipelineSpec {
    // D132: Video-Branch — der Submit liefert den echten Asset-Kind (resolveInputs, Slot-Union).
    if (inputs[0]?.kind === "video") {
      // D146: AI-Matting (BEN2) ODER chroma-key — beide liefern RGBA-Frames → video-encode.
      const aiBgRemove = settings.method === "ai";
      let removeNode: PipelineNode;
      if (aiBgRemove) {
        // D34: Provider gepinnt (Build==Laufzeit) — der Default-Adapter des Slots (BEN2).
        const ben2 = ctx.providers.resolve<VideoBgRemoveProvider>("video-bg-remove");
        removeNode = {
          id: "key",
          capabilityId: CAP.videoBgRemove.id,
          capabilityVersion: CAP.videoBgRemove.version,
          surface: "server",
          inputs: { video: { node: "$input", port: "source" } },
          params: { providerId: ben2.id, providerVersion: ben2.version, fps: VIDEO_FPS },
        };
      } else {
        removeNode = {
          id: "key",
          capabilityId: CAP.chromaKey.id,
          capabilityVersion: CAP.chromaKey.version,
          surface: "server",
          inputs: { video: { node: "$input", port: "source" } },
          // fps geht in den capability-internen Decode (D38) und dessen GPU-Key.
          params: { tolerance: settings.tolerance, keyColor: settings.keyColor, fps: VIDEO_FPS },
        };
      }
      const nodes: PipelineNode[] = [
        removeNode,
        {
          id: "encode",
          capabilityId: CAP.videoEncode.id,
          capabilityVersion: CAP.videoEncode.version,
          surface: "server",
          inputs: { frames: { node: "key", port: "frames" } },
          params: { fps: VIDEO_FPS },
        },
      ];
      return { nodes, outputs: { video: { ref: { node: "encode", port: "video" } } } };
    }
    const nodes: PipelineNode[] = [
      {
        id: "key",
        capabilityId: CAP.chromaKey.id,
        capabilityVersion: CAP.chromaKey.version,
        surface: "server",
        inputs: { image: { node: "$input", port: "source" } },
        params: { tolerance: settings.tolerance, keyColor: settings.keyColor },
      },
    ];
    return { nodes, outputs: { image: { ref: { node: "key", port: "image" } } } };
  },
};
